import React, { useState, useRef } from 'react';
import { Modal, Input, Alert, message } from 'antd';
import { createTemplateFromVersion } from '../../services/Apis';

interface CreateTemplateModalProps {
  open: boolean;
  dealId: number;
  versionName: string;
  isOldBasePricing: boolean;
  exampleResource?: string;
  onClose: () => void;
  onSuccess: (templateId: number, templateName: string) => void;
  onTokenExpired: () => void;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  open,
  dealId,
  versionName,
  isOldBasePricing,
  exampleResource,
  onClose,
  onSuccess,
  onTokenExpired,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  // resetKey remounts the inputs with empty defaultValues on close/success — no re-render on every keystroke
  const [resetKey, setResetKey] = useState<number>(0);
  const templateNameRef = useRef<string>('');
  const templateDescRef = useRef<string>('');

  const reset = () => {
    templateNameRef.current = '';
    templateDescRef.current = '';
    setResetKey((k) => k + 1);
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleOk = async () => {
    const name = templateNameRef.current.trim();
    const desc = templateDescRef.current.trim();
    if (!name) {
      message.error('Template name is required');
      return;
    }
    setLoading(true);
    const result = await createTemplateFromVersion(dealId, versionName, name, desc);
    setLoading(false);
    if (result.error_status === 401) {
      onTokenExpired();
    } else if (result.error) {
      message.error(result.error);
    } else {
      message.success('Template created successfully');
      reset();
      onSuccess(result.template_id, result.template_name);
    }
  };

  return (
    <Modal
      title="Save as Template"
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText="Create Template"
      zIndex={10000}
    >
      <Alert
        type="warning"
        showIcon
        message="Only saved data will be included in the template. Any unsaved changes on this page will not be reflected."
        style={{ marginBottom: 12 }}
      />
      {isOldBasePricing && (
        <Alert
          type="error"
          showIcon
          message="Base Pricing Version Mismatch"
          description={`This deal uses an older base pricing version. Some resources may not be available in the template, as it will use the latest version.${exampleResource ? ` (e.g. '${exampleResource}')` : ''}`}
          style={{ marginBottom: 16 }}
        />
      )}
      <div style={{ marginBottom: 16 }}>
        <label>Template Name <span style={{ color: 'red' }}>*</span></label>
        <Input
          key={`name-${resetKey}`}
          defaultValue=""
          onChange={(e) => { templateNameRef.current = e.target.value; }}
          placeholder="Enter template name"
          style={{ marginTop: 4 }}
        />
      </div>
      <div>
        <label>Description <span style={{ color: '#8c8c8c' }}>(optional)</span></label>
        <Input.TextArea
          key={`desc-${resetKey}`}
          defaultValue=""
          onChange={(e) => { templateDescRef.current = e.target.value; }}
          placeholder="Briefly describe what this template is for"
          rows={3}
          style={{ marginTop: 4 }}
        />
      </div>
    </Modal>
  );
};

export default CreateTemplateModal;
